import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Info, Phone } from "lucide-react";

import defaultImg from "../Images/swing.jpeg";
import img from "../Images/dualpl.jpeg";
import img1 from "../Images/wafer.jpeg";
import img2 from "../Images/swingspec.png";
import img3 from "../Images/dualplatespec.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6">
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">{product.name}</h3>
      </header>
      
      <div className="bg-gray-100 h-40 sm:h-48 md:h-56 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - industrial check valve for backflow prevention`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          loading="lazy"
          width="400"
          height="300"
        />
      </div>

      <div className="mb-4">
        <nav className="flex border-b border-gray-200" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'description'}
            aria-controls={`${product.id}-description`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'description'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('description')}
          >
            Description
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'specs'}
            aria-controls={`${product.id}-specs`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'specs'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('specs')}
          >
            Specifications
          </button>
        </nav>

        <div className="mt-4">
          {activeTab === 'description' ? (
            <div id={`${product.id}-description`} role="tabpanel">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-justify">
                {product.description}
              </p>
            </div>
          ) : (
            <div id={`${product.id}-specs`} role="tabpanel">
              <div className="bg-gray-100 h-32 sm:h-40 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} technical specifications and dimensions`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="400"
                  height="200"
                />
              </div>
              <ul className="space-y-2">
                {product.specifications.map((spec, index) => (
                  <li key={index} className="flex items-start">
                    <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-xs sm:text-sm text-gray-600">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const NonReturnValves = () => {
  const handleCall = () => {
    window.location.href = 'tel:+9779802781400';
  };

  const products = [
    {
      id: "swing-check",
      name: "Swing Check Valve",
      mainImage: defaultImg,
      specImage: img2,
      description: `Gortherm Energy's Swing Check Valves are designed with a hinged disc that swings open with forward flow and closes automatically with reverse flow, preventing backflow in pipelines. These valves feature robust construction with a freely swinging disc mechanism that provides reliable operation and minimal pressure drop. The design incorporates high-quality materials and precision engineering to ensure long-term performance in demanding industrial applications including water, steam, and oil systems. Our swing check valves are particularly effective in horizontal pipeline installations where consistent flow and backflow prevention are crucial for system integrity.`,
      specifications: [
        'Full-port design for maximum flow capacity',
        'Self-actuating operation mechanism',
        'Low pressure drop characteristics',
        'Multiple material options available',
        'Suitable for horizontal installations',
        'Quick closing response to reverse flow'
      ]
    },
    {
      id: "dual-plate",
      name: "Dual Plate Check Valves",
      mainImage: img,
      specImage: img3,
      description: `Our Dual Plate Check Valves represent advanced engineering in non-return valve technology. These valves feature two semi-circular plates hinged at the center, providing superior performance in preventing reverse flow while minimizing water hammer effects. The innovative design ensures faster closure and reduced wear compared to traditional check valves, making them ideal for high-cycle applications. Gortherm Energy manufactures these valves with high-grade materials to withstand challenging operating conditions while maintaining reliable performance. The dual plate design offers advantages such as compact installation length, lighter weight, and more efficient operation in both vertical and horizontal installations.`,
      specifications: [
        'Compact and lightweight design',
        'Rapid response to flow reversal',
        'Spring-assisted closure mechanism',
        'Lower maintenance requirements',
        'Space-saving installation profile',
        'Enhanced sealing capabilities'
      ]
    },
    {
      id: "wafer-check",
      name: "Wafer Check Valves",
      mainImage: img1,
      specImage: img3,
      description: `Wafer Check Valves from Gortherm Energy combine efficiency with space-saving design. These valves feature a compact wafer-style body that fits between pipeline flanges, making them ideal for installations where space is limited and easy maintenance is required. The streamlined design minimizes pressure drop while ensuring reliable backflow prevention in water, chemical, and industrial process systems. Our wafer check valves incorporate precision-engineered components and high-quality materials to deliver consistent performance in various industrial applications. The design allows for easy installation and maintenance, reducing operational costs while maintaining system integrity and preventing reverse flow contamination.`,
      specifications: [
        'Space-efficient wafer design',
        'Easy installation between flanges',
        'Minimal pressure loss',
        'Cost-effective solution',
        'Various material options',
        'Suitable for multiple applications'
      ]
    }
  ];

  const productListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Non-Return Valves",
    "description": "Comprehensive range of check valves including swing, dual plate, and wafer designs for backflow prevention",
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.mainImage,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "price": "Contact for pricing",
          "priceCurrency": "USD"
        }
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Non-Return Valves",
        "item": "https://www.gorthermenergy.com/products/non-return-valves"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Non-Return Check Valves | Swing & Wafer Valves Nepal</title>
        <meta name="description" content="Premium non-return check valves including swing, dual plate & wafer designs. Backflow prevention solutions in Nepal." />
        <meta name="keywords" content="non-return valves, check valves, swing check valve, dual plate valve, wafer check valve, backflow prevention, Nepal" />
        <link rel="canonical" href="https://www.gorthermenergy.com/products/non-return-valves" />
        <html lang="en" />
        <meta property="og:title" content="Non-Return Check Valves | Gortherm Energy" />
        <meta property="og:description" content="Industrial check valves for backflow prevention in water, steam, and process systems." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/products/non-return-valves" />
        <meta property="og:image" content={defaultImg} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">
          {JSON.stringify(productListSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          
          <header className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Non-Return Check Valves
            </h1>
            <h2 className="text-xl sm:text-2xl text-gray-700 mb-4">
              Backflow Prevention Solutions
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Discover our comprehensive range of non-return check valves and backflow prevention solutions, engineered to meet the demanding requirements of modern industrial processes across water treatment, steam systems, and chemical processing applications in Nepal.
            </p>
          </header>

          <section aria-label="Non-return valves product catalog">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>

          <section className="mt-12 sm:mt-16 bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Why Choose Our Check Valves?
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Reliable Backflow Prevention</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Automatic closure mechanisms ensure reliable backflow prevention protecting pumps, equipment, and system integrity.
                </p>
              </article>
              
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Low Maintenance</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Simple design with few moving parts ensures long service life and minimal maintenance requirements.
                </p>
              </article>
              
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Wide Application</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Suitable for water, steam, oil, gas, and chemical services across various industrial applications.
                </p>
              </article>
            </div>
          </section>

          <section className="mt-12 sm:mt-16 text-center bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Need Expert Guidance?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our technical experts are ready to help you select the right check valve for your specific application and operating conditions.
            </p>
            <button
              onClick={handleCall}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <span className="text-sm sm:text-base font-semibold">Request Consultation</span>
              <Phone className="w-5 h-5" />
            </button>
            
          </section>
        </div>
      </div>
    </>
  );
};

export default NonReturnValves;